import { Button, Checkbox, Flex, FormControl, FormErrorMessage, FormHelperText, Tooltip } from '@chakra-ui/react'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'

import { FormGroup } from '../components/FormGroup'
import { FormLabel } from '../components/FormLabel'
import { FormSpacer } from '../components/FormSpacer'
import { InputGroup } from '../components/InputGroup'
import { PhoneInputGroup } from '../components/PhoneInputGroup'
import { Cloud, Info } from '../icons'
import { InsureeAddress } from './InsureeAddress'
import { InsureeName } from './InsureeName'
import { InsureeTypeSme } from './InsureeTypeSme'

export interface AdditionalInsureeSmeInfoProps {
  hideContacts?: boolean
}

export const AdditionalInsureeSmeInfo = ({ hideContacts = false }: AdditionalInsureeSmeInfoProps) => {
  const { getFieldState, register, watch } = useFormContext()
  const businessId = watch('businessId')
  const [fetchClient, setFetchClient] = useState<{
    client: string | null
    loading: boolean
    state: 'error' | 'success' | null
  }>({
    client: null,
    loading: false,
    state: null,
  })

  useEffect(() => {
    if (!businessId) {
      setFetchClient({
        client: null,
        loading: false,
        state: null,
      })
    }
  }, [businessId])

  const errors = getFieldState(businessId).error

  return (
    <>
      <InsureeTypeSme />

      <FormSpacer />

      <FormGroup>
        <Flex sx={{ columnGap: 8, rowGap: 2, flexWrap: 'wrap', alignItems: 'start' }}>
          <FormControl>
            <FormLabel>IČO</FormLabel>

            <InputGroup placeholder="Např. 00000000" errors={errors} {...register('businessId')} />

            {typeof errors === 'undefined' && (
              <FormHelperText>Zadejte číslice bez mezer, lomítek či pomlček.</FormHelperText>
            )}
          </FormControl>

          <FormControl isInvalid={fetchClient.state === 'error'}>
            <FormLabel>&nbsp;</FormLabel>

            <Button
              type="button"
              variant="outline"
              isLoading={fetchClient.loading}
              isDisabled={fetchClient.loading || !businessId}
              onClick={() => {
                setFetchClient({ ...fetchClient, loading: true })

                setTimeout(() => {
                  setFetchClient({ ...fetchClient, loading: false, state: 'error' })
                }, 1000)
              }}
            >
              <Cloud color="secondary.500" />
              Načíst údaje o klientovi
            </Button>

            {fetchClient.state === 'error' && !fetchClient.loading && (
              <FormErrorMessage>Klient nenalezen.</FormErrorMessage>
            )}
          </FormControl>
        </Flex>
      </FormGroup>

      <FormGroup>
        <FormControl>
          <FormLabel>Název firmy</FormLabel>

          <InputGroup placeholder="Např. Firma, s.r.o." {...register('comapnyName')} />
        </FormControl>
      </FormGroup>

      <FormGroup>
        <FormControl>
          <FormLabel>Titul před jménem</FormLabel>

          <InputGroup placeholder="Např. Ing." htmlSize={8} {...register('namePrefix')} />
        </FormControl>

        <InsureeName personPrefix='TODO' />
      </FormGroup>

      <FormSpacer />

      <>
        <FormGroup>
          <FormControl>
            <FormLabel>Ulice</FormLabel>

            <InputGroup placeholder="Např. Dlouhá" htmlSize={24} {...register('street')} />
          </FormControl>

          <FormControl>
            <FormLabel>Číslo popisné nebo orientační</FormLabel>

            <InputGroup placeholder="Např. 123" htmlSize={4} {...register('houseNumber')} />
          </FormControl>
        </FormGroup>

        <InsureeAddress addressPrefix='TODO' />
      </>

      {!hideContacts && (
        <>
          <FormSpacer />

          <FormGroup>
            <FormControl>
              <FormLabel>Mobilní telefon</FormLabel>

              <PhoneInputGroup
                select={register('mobilePhonePrefix')}
                input={register('mobilePhoneNumber')}
              />
            </FormControl>

            <FormControl>
              <FormLabel optional>Pevná linka</FormLabel>

              <PhoneInputGroup
                select={register('landlinePrefix')}
                input={register('landlineNumber')}
              />
            </FormControl>

            <FormControl>
              <FormLabel>E-mail</FormLabel>

              <InputGroup type="email" {...register('email')} />
            </FormControl>
          </FormGroup>

          <Flex sx={{ gap: 2 }}>
            <Checkbox {...register('acceptECommunication')}>
              Souhlas s elektronickou komunikací
            </Checkbox>
            <Tooltip
              shouldWrapChildren
              hasArrow
              label="Souhlas s elektronickou komunikací"
              placement="top"
            >
              <Info color="blue.500" />
            </Tooltip>
          </Flex>
        </>
      )}
    </>
  )
}
